<script>
import Layout from '@/components/layout/app-layout'
import appConfig from '@/app.config'
import PageHeader from '@/components/layout/page-header'

import api from '@/helpers/api'

export default {
  name: 'sales.customers.form',
  page: {
    title: 'Sales - Customer',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  components: {
    Layout,
    PageHeader
  },
  props: {
    customer_id: { type: String }
  },
  data: () => ({
    isLoading: false,
    customer: {
      billing_address: {}
    }
  }),
  beforeMount () {
    this.onInit()
  },
  watch: {
    customer_id: 'onInit'
  },
  methods: {
    async onInit () {
      if (this.isUpdate) {
        this.isLoading = true
        let item = await api.customers.fetch({
          id: this.customer_id
        })
        this.customer = {
          ...item,
          billing_email: item.billing_email.join(';')
        }
        this.isLoading = false
      } else {
        let customer_number = await api.customers.nextNumber()
        this.customer = {
          billing_address: {},
          customer_number
        }
      }
    },
    async onSave () {
      let valid = true

      if (valid) {
        let number = this.customer.customer_number
        if (!this.isUpdate) number = await api.customers.nextNumber()
        const { legal_name } = this.customer
        const { attn, line1, line2, zipcode, city, country_code } =
          this.customer.billing_address

        const data = {
          ...this.customer,
          billing_email: this.customer.billing_email.split(';'),
          customer_number: number
        }

        try {
          this.isLoading = true
          let item
          if (this.isUpdate) {
            let address = await api.addresses.update({
              id: this.customer.billing_address_id,
              attn,
              line1,
              line2,
              zipcode,
              city,
              country_code
            })

            item = api.customers.update({
              ...data
            })
          } else {
            let address = await api.addresses.create({
              alias: legal_name,
              attn,
              company_name: legal_name,
              line1,
              line2,
              zipcode,
              city,
              country_code
            })
            item = await api.customers.create({
              ...data,
              billing_address_id: address.id
            })
          }
          this.isLoading = false
          this.$router.push({ name: 'sales.customers' })
        } catch (err) {
          console.error(err)
          this.isLoading = false
        }
      }
    }
  },
  computed: {
    isUpdate () {
      return this.customer_id !== undefined
    },
    title () {
      return this.isUpdate ? 'Edit Customer' : 'New Customer'
    },
    saveBtnLabel () {
      return this.isUpdate ? 'Save Changes' : 'Create Customer'
    },
    breadcrumbs () {
      return [
        {
          text: 'Sales',
          to: { name: 'sales' }
        },
        {
          text: 'Customers',
          to: { name: 'sales.customers' }
        },
        {
          text: this.customer.legal_name
            ? `${this.customer.legal_name}`
            : 'New Customer',
          active: true
        }
      ]
    }
  }
}
</script>
<template>
  <Layout>
    <PageHeader :title="title" :items="breadcrumbs">
      <b-btn variant="outline-secondary" class="mr-2" @click="$router.back()"
        >Cancel</b-btn
      >
      <b-btn variant="primary" class="btn-label" @click="onSave">
        <i class="bx bxs-save label-icon"></i>
        {{ saveBtnLabel }}
      </b-btn>
    </PageHeader>

    <div class="card">
      <div class="card-body">
        <h4 class="card-title">Basic Information</h4>
        <p class="card-title-desc">Fill all information below</p>
        <div class="row">
          <div class="col-sm-6">
            <b-form-group
              label="Customer N°"
              label-for="customer_number"
              label-cols-sm="3"
            >
              <b-form-input
                id="customer_number"
                v-model="customer.customer_number"
                type="text"
                required
                readonly
                :style="{ width: '150px' }"
              ></b-form-input>
            </b-form-group>
            <b-form-group
              label="VAT N°"
              label-for="vat_number"
              label-cols-sm="3"
            >
              <b-form-input
                id="vat_number"
                v-model="customer.vat_number"
                type="text"
                required
                autocomplete="vat_number"
                placeholder="Ex. BE27897439743"
                :style="{ width: '350px' }"
              ></b-form-input>
            </b-form-group>
            <b-form-group
              label="Legal Name"
              label-for="legal_name"
              label-cols-sm="3"
            >
              <b-form-input
                id="legal_name"
                v-model="customer.legal_name"
                type="text"
                required
                autocomplete="legal_name"
                placeholder="Legal name of the company"
                :style="{ width: '350px' }"
              ></b-form-input>
            </b-form-group>

            <b-form-group
              label="Email"
              label-for="primary_email"
              label-cols-sm="3"
            >
              <b-form-input
                id="primary_email"
                v-model="customer.primary_email"
                type="email"
                required
                autocomplete="new--password"
                placeholder="Primary Email address"
                :style="{ width: '350px' }"
              ></b-form-input>
            </b-form-group>
            <b-form-group
              label="Phone"
              label-for="primary_phone"
              label-cols-sm="3"
            >
              <b-form-input
                id="primary_phone"
                v-model="customer.primary_phone"
                type="text"
                required
                autocomplete="primary_phone"
                placeholder="ex. +32 4565663"
                :style="{ width: '350px' }"
              ></b-form-input>
            </b-form-group>
          </div>
          <div class="col-sm-6">
            <b-form-group label="Attn." label-for="attn" label-cols-sm="3">
              <b-form-input
                id="line1"
                v-model="customer.billing_address.attn"
                type="text"
                placeholder="Name of Person or Department"
                autocomplete="attn"
                :style="{ width: '350px' }"
              ></b-form-input>
            </b-form-group>
            <b-form-group
              label="Addressline 1"
              label-for="line1"
              label-cols-sm="3"
            >
              <b-form-input
                id="line1"
                v-model="customer.billing_address.line1"
                type="text"
                required
                autocomplete="addressline1"
                placeholder="Street and number"
                :style="{ width: '450px' }"
              ></b-form-input>
            </b-form-group>
            <b-form-group
              label="Addressline 2"
              label-for="line2"
              label-cols-sm="3"
            >
              <b-form-input
                id="line2"
                v-model="customer.billing_address.line2"
                type="text"
                autocomplete="addressline2"
                placeholder="Additional info ex. bus, floor, department"
                :style="{ width: '450px' }"
              ></b-form-input>
            </b-form-group>

            <b-form-group label="Zipcode" label-for="zipcode" label-cols-sm="3">
              <b-form-input
                id="zipcode"
                v-model="customer.billing_address.zipcode"
                type="text"
                required
                autocomplete="zipcode"
                placeholder="Zipcode"
                :style="{ width: '150px' }"
              ></b-form-input>
            </b-form-group>

            <b-form-group label="City" label-for="city" label-cols-sm="3">
              <b-form-input
                id="city"
                v-model="customer.billing_address.city"
                type="text"
                required
                autocomplete="city"
                placeholder="City"
                :style="{ width: '280px' }"
              ></b-form-input>
            </b-form-group>
            <b-form-group
              label="Country"
              label-for="country_code"
              label-cols-sm="3"
            >
              <app-country-select
                id="country_code"
                v-model="customer.billing_address.country_code"
                :style="{ width: '50%' }"
              />
            </b-form-group>
          </div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-body">
        <h4 class="card-title">Billing Settings</h4>
        <p class="card-title-desc">Additional Customer Options and Settings</p>
        <b-form-group
          label="EU Vat Exempt"
          label-for="eu_vat_exempt"
          label-cols-sm="3"
          class="align-items-center"
        >
          <template v-slot:label>
            <div class="d-flex align-items-center">
              EU Vat Exempt
              <i
                class="bx bx-info-circle mx-4 font-size-20"
                id="info_eu_vat_exempt"
              ></i>
            </div>
          </template>
          <b-form-checkbox
            switch
            size="lg"
            id="eu_vat_exempt"
            v-model="customer.eu_vat_exempt"
            name="eu_vat_exempt"
          ></b-form-checkbox>
          <b-tooltip
            target="info_eu_vat_exempt"
            triggers="hover"
            placement="right"
            >Enable this option if the customer's Headquarters is located in
            EU-Country.</b-tooltip
          >
        </b-form-group>
        <b-form-group
          label-for="send_digital_invoice"
          label-cols-sm="3"
          class="d-flex align-items-center"
        >
          <template v-slot:label>
            <div class="d-flex align-items-center pointer">
              Send Digital Invoice
              <i
                class="bx bx-info-circle mx-4 font-size-20"
                id="info_send_digital_invoice"
              ></i>
            </div>
          </template>
          <b-form-checkbox
            switch
            size="lg"
            id="send_digital_invoice"
            v-model="customer.send_digital_invoice"
            name="send_digital_invoice"
            inline
          ></b-form-checkbox>
          <b-tooltip
            target="info_send_digital_invoice"
            triggers="hover"
            placement="right"
            >Enable this option if the customer's wants to receive a digital
            invoice.</b-tooltip
          >
        </b-form-group>
        <b-form-group
          label="Billing Email"
          label-for="invoice_emails"
          label-cols-sm="3"
          class="d-flex align-items-center"
          v-if="customer.send_digital_invoice"
        >
          <b-form-input
            id="invoice_emails"
            v-model="customer.billing_email"
            type="text"
            required
            autocomplete="invoice_emails"
            placeholder="ex. invoice@company.com;accounting@company.com"
            :style="{ maxWidth: '800px' }"
          ></b-form-input>
        </b-form-group>
        <b-form-group
          v-if="false"
          label="Payment Term"
          label-for="payment_term"
          label-cols-sm="3"
          class="d-flex align-items-center"
        >
          <app-payment-term-select v-model="customer.payment_term_id" />
        </b-form-group>
        <b-form-group
          label-for="send_digital_invoice"
          label-cols-sm="3"
          class="d-flex align-items-center"
        >
          <template v-slot:label>
            <div class="d-flex align-items-center pointer">
              Structured Reference
              <i
                class="bx bx-info-circle mx-4 font-size-20"
                id="info_use_structured_reference"
              ></i>
            </div>
          </template>
          <b-form-checkbox
            switch
            size="lg"
            id="use_structured_reference"
            v-model="customer.use_structured_reference"
            name="use_structured_reference"
            inline
          ></b-form-checkbox>
          <b-tooltip
            target="info_use_structured_reference"
            triggers="hover"
            placement="right"
            >Use Structured Reference on Invoice.</b-tooltip
          >
        </b-form-group>
        <h4 class="card-title my-4">Other Settings</h4>

        <b-form-group
          label-for="is_favorite"
          label-cols-sm="3"
          class="d-flex align-items-center"
        >
          <template v-slot:label>
            <div class="d-flex align-items-center pointer">
              <i class="bx bx-archive mr-2 font-size-20"></i>Archived
            </div>
          </template>
          <b-form-checkbox
            switch
            size="lg"
            id="is_archived"
            v-model="customer.is_archived"
            name="is_archived"
            inline
          ></b-form-checkbox>
        </b-form-group>
      </div>
    </div>

    <b-row class="mb-4">
      <b-col cols="12" class="d-flex align-items-center justify-content-end">
        <b-btn variant="outline-secondary" class="mr-2" @click="$router.back()"
          >Cancel</b-btn
        >
        <b-btn variant="primary" class="btn-label" @click="onSave">
          <i class="bx bxs-save label-icon"></i>
          {{ saveBtnLabel }}
        </b-btn>
      </b-col>
    </b-row>
  </Layout>
</template>
